import { useUser, useSignIn, useSignUp } from '@clerk/clerk-react';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { T, useTranslate } from '@tolgee/react';
import { useHubspotContactId } from '~/helper/useHubspotContactId';
import { LegacyOtpInput } from '~/components/LegacyOtpInput';
import { LoadingSpinner } from '~/components/global/LoadingSpinner';
import {
  isTurnstileError,
  TURNSTILE_USER_MESSAGE,
  TURNSTILE_USER_MESSAGE_KEY,
} from '~/components/auth/turnstileError';

/**
 * Helper to clear stale Clerk SSO state from sessionStorage
 * Prevents race conditions from previous failed SSO attempts
 */
function clearStaleClerkState(): void {
  try {
    const keysToCheck = Object.keys(sessionStorage);
    for (const key of keysToCheck) {
      if (key.startsWith('clerk') && key.includes('sign')) {
        sessionStorage.removeItem(key);
      }
    }
  } catch {
    // Ignore storage errors
  }
}

/**
 * Segment-Guard: prueft, ob ein Pfad mindestens drei nicht-leere Segmente hat
 * (/<lang>/<companyId>/<contactId>). Verhindert Navigation zu unvollstaendigen
 * Dashboard-URLs (z.B. "/de/70927429839" ohne Contact-ID-Segment), die auf der
 * Zielroute ($lang.$companyId/$contactId/...) zu "Unerwarteter Fehler" fuehren,
 * weil die Route ohne Contact-ID-Segment schlicht nicht existiert.
 */
function hasValidDashboardSegments(path: string | undefined | null): boolean {
  if (!path || typeof path !== 'string') return false;
  if (path.includes('//')) return false;
  const segments = path.split('/').filter((segment) => segment.length > 0);
  if (segments.length < 3) return false;
  return segments
    .slice(0, 3)
    .every((segment) => segment !== 'undefined' && segment !== 'null');
}

/**
 * Baut aus einem (moeglicherweise unvollstaendigen, nur lang+companyId
 * enthaltenden) Dashboard-Pfad und einer bekannten HubSpot-Contact-ID die
 * korrekte Ziel-URL /<lang>/<companyId>/<contactId> zusammen. Liefert null,
 * wenn lang/companyId oder die Contact-ID nicht sicher ermittelbar sind —
 * in dem Fall darf NICHT navigiert werden.
 */
function buildContactDashboardUrl(
  basePath: string | undefined | null,
  hubspotContactId: unknown,
): string | null {
  if (!basePath || typeof basePath !== 'string') return null;
  if (hubspotContactId === undefined || hubspotContactId === null) return null;
  const contactSegment = String(hubspotContactId).trim();
  if (
    !contactSegment ||
    contactSegment === 'undefined' ||
    contactSegment === 'null'
  ) {
    return null;
  }
  const segments = basePath.split('/').filter((segment) => segment.length > 0);
  const [
    lang,
    companyId,
  ] = segments;
  if (!lang || !companyId) return null;
  const candidate = `/${lang}/${companyId}/${contactSegment}`;
  return hasValidDashboardSegments(candidate) ? candidate : null;
}

interface LoginWidgetProps {
  dashboardUrl: string;
  initialSSOView?: boolean;
}

export function LegacyLoginWidget({
  dashboardUrl,
  initialSSOView = false,
}: LoginWidgetProps) {
  const { t } = useTranslate();
  const { isSignedIn, isLoaded } = useUser();
  const navigate = useNavigate();
  const { signIn, setActive } = useSignIn();
  const { signUp } = useSignUp();
  const { hubspotContactId, timedOut: hubspotTimedOut } = useHubspotContactId();
  const [
    isWaitingForHubspot,
    setIsWaitingForHubspot,
  ] = useState(false);

  const [
    email,
    setEmail,
  ] = useState('');
  const [
    code,
    setCode,
  ] = useState('');
  const [
    isLoading,
    setIsLoading,
  ] = useState(false);
  const [
    error,
    setError,
  ] = useState('');
  const [
    step,
    setStep,
  ] = useState<'email' | 'code'>('email');
  const [
    authFlow,
    setAuthFlow,
  ] = useState<'signIn' | 'signUp'>('signIn');
  const [
    isSSOView,
    setIsSSOView,
  ] = useState(initialSSOView);
  const [
    ssoMessage,
    setSsoMessage,
  ] = useState<string | null>(null);
  const isVerifyingRef = useRef(false);

  // Check for SSO retry/error parameters from failed callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoRetry = params.get('sso_retry');
    const ssoIncomplete = params.get('sso_incomplete');
    const ssoError = params.get('sso_error');

    if (ssoRetry || ssoIncomplete || ssoError) {
      // Clear any stale state that caused the issue
      clearStaleClerkState();

      // Show appropriate message
      if (ssoRetry) {
        setSsoMessage(
          t(
            'auth_signin_sso_retry',
            'Die SSO-Anmeldung konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut.',
          ),
        );
      } else if (ssoIncomplete) {
        setSsoMessage(
          t(
            'auth_signin_sso_incomplete',
            'Die Anmeldung wurde nicht abgeschlossen. Bitte versuchen Sie es erneut.',
          ),
        );
      } else if (ssoError) {
        setSsoMessage(
          t(
            'auth_signin_sso_error',
            'Bei der SSO-Anmeldung ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
          ),
        );
      }

      // Switch to SSO view if we came from SSO
      if (initialSSOView || ssoRetry) {
        setIsSSOView(true);
      }

      // Clean up URL parameters
      const url = new URL(window.location.href);
      url.searchParams.delete('sso_retry');
      url.searchParams.delete('sso_incomplete');
      url.searchParams.delete('sso_error');
      window.history.replaceState({}, '', url.toString());
    }
  }, [
    initialSSOView,
    t,
  ]);

  useEffect(() => {
    // Im Sign-Up-Flow existiert die HubSpot-ID noch nicht (sie entsteht erst
    // asynchron per Webhook) — der Wait-Guard wuerde dort endlos warten.
    if (
      isLoaded &&
      isSignedIn &&
      !isWaitingForHubspot &&
      authFlow !== 'signUp'
    ) {
      // Segment-Guard: nie zu einer URL mit weniger als drei Pfadsegmenten
      // navigieren (z.B. "/de/70927429839" ohne Contact-ID -> Fehlerseite).
      if (hasValidDashboardSegments(dashboardUrl)) {
        void navigate({ to: dashboardUrl as any, replace: true });
        return;
      }
      const correctedUrl = buildContactDashboardUrl(
        dashboardUrl,
        hubspotContactId,
      );
      if (correctedUrl) {
        void navigate({ to: correctedUrl as any, replace: true });
        return;
      }
      // dashboardUrl ist unvollstaendig und kann (noch) nicht sicher
      // korrigiert werden -> weiter warten statt kaputt zu navigieren.
      setIsWaitingForHubspot(true);
    }
  }, [
    isLoaded,
    isSignedIn,
    navigate,
    dashboardUrl,
    hubspotContactId,
    isWaitingForHubspot,
    authFlow,
  ]);

  useEffect(() => {
    if (
      isWaitingForHubspot &&
      hubspotContactId &&
      dashboardUrl &&
      dashboardUrl !== '//'
    ) {
      // Segment-Guard: nie zu einer URL mit weniger als drei Pfadsegmenten
      // navigieren.
      if (hasValidDashboardSegments(dashboardUrl)) {
        void navigate({ to: dashboardUrl as any, replace: true });
        return;
      }
      const correctedUrl = buildContactDashboardUrl(
        dashboardUrl,
        hubspotContactId,
      );
      if (correctedUrl) {
        void navigate({ to: correctedUrl as any, replace: true });
      }
      // Sonst: unvollstaendige URL kann nicht sicher korrigiert werden ->
      // isWaitingForHubspot bleibt true, Ladehinweis bleibt sichtbar.
    }
  }, [
    isWaitingForHubspot,
    hubspotContactId,
    dashboardUrl,
    navigate,
  ]);

  const goToDashboard = useCallback(() => {
    if (!hubspotContactId || !dashboardUrl || dashboardUrl === '//') {
      setIsWaitingForHubspot(true);
      return;
    }
    // Segment-Guard: nie zu einer URL mit weniger als drei Pfadsegmenten
    // navigieren (z.B. "/de/70927429839" ohne Contact-ID -> Fehlerseite).
    if (hasValidDashboardSegments(dashboardUrl)) {
      void navigate({ to: dashboardUrl as any, replace: true });
      return;
    }
    const correctedUrl = buildContactDashboardUrl(
      dashboardUrl,
      hubspotContactId,
    );
    if (correctedUrl) {
      void navigate({ to: correctedUrl as any, replace: true });
      return;
    }
    // dashboardUrl bleibt unvollstaendig und kann nicht sicher korrigiert
    // werden -> weiter warten statt kaputt zu navigieren.
    setIsWaitingForHubspot(true);
  }, [
    navigate,
    dashboardUrl,
    hubspotContactId,
  ]);

  const verifyCode = useCallback(
    async (codeToVerify: string) => {
      if (isVerifyingRef.current) return;
      isVerifyingRef.current = true;
      setIsLoading(true);
      setError('');

      try {
        let result;
        if (authFlow === 'signIn') {
          result = await signIn.attemptFirstFactor({
            strategy: 'email_code',
            code: codeToVerify,
          });
        } else {
          result = await signUp.attemptEmailAddressVerification({
            code: codeToVerify,
          });
        }

        if (result.status === 'complete') {
          if (setActive) {
            await setActive({ session: result.createdSessionId });
          }
          isVerifyingRef.current = false;
          setTimeout(() => {
            goToDashboard();
          }, 100);
        } else if (result.status === 'missing_requirements') {
          const missingFields: string[] = result.missingFields || [];
          console.log(
            'Verification successful but requirements missing:',
            'missingFields:',
            missingFields,
            result,
          );

          if (authFlow === 'signUp') {
            // Case 1: Only legal_accepted is missing — auto-complete it
            if (
              missingFields.length === 1 &&
              missingFields[0] === 'legal_accepted'
            ) {
              try {
                const completeResult = await signUp.update({
                  legalAccepted: true,
                });

                if (completeResult.status === 'complete') {
                  if (setActive) {
                    await setActive({
                      session: completeResult.createdSessionId,
                    });
                  }
                  isVerifyingRef.current = false;
                  setTimeout(() => {
                    goToDashboard();
                  }, 100);
                  return;
                }
              } catch (updateErr) {
                console.error(
                  'Failed to auto-complete legal_accepted:',
                  updateErr,
                );
              }
            }

            // Case 2: Other fields missing (password, first_name, last_name, etc.)
            if (missingFields.length > 0) {
              const fieldLabels: Record<string, string> = {
                password: t('auth_signin_field_password', 'Passwort'),
                first_name: t('auth_signin_field_first_name', 'Vorname'),
                last_name: t('auth_signin_field_last_name', 'Nachname'),
                legal_accepted: t(
                  'auth_signin_field_legal_accepted',
                  'AGB-Zustimmung',
                ),
              };
              const fieldNames = missingFields
                .map((f) => fieldLabels[f] || f)
                .join(', ');
              setError(
                t(
                  'auth_signin_error_registration_incomplete',
                  'Registrierung unvollständig. Es fehlen: {fields}. Bitte registrieren Sie sich über das Registrierungsformular.',
                  { fields: fieldNames },
                ),
              );
              // Reset to email step so user is not stuck on the dead 'code' screen
              setStep('email');
              setCode('');
              setAuthFlow('signIn');
              isVerifyingRef.current = false;
              setIsLoading(false);
              return;
            }

            // Case 3: createdSessionId fallback (existing path)
            if (result.createdSessionId && setActive) {
              await setActive({ session: result.createdSessionId });
              isVerifyingRef.current = false;
              setTimeout(() => {
                goToDashboard();
              }, 100);
              return;
            }
          }

          console.error(
            'Could not complete flow with missing requirements:',
            result,
          );
          setError(
            t(
              'auth_signin_error_account_not_created',
              'Verifizierung erfolgreich, aber Konto konnte nicht erstellt werden. Fehlende Angaben.',
            ),
          );
          isVerifyingRef.current = false;
          setIsLoading(false);
        } else {
          console.error('Verification status incomplete:', result.status);
          setError(
            t(
              'auth_signin_error_verification_incomplete',
              'Verifizierung nicht abgeschlossen. Bitte versuchen Sie es erneut.',
            ),
          );
          isVerifyingRef.current = false;
          setIsLoading(false);
        }
      } catch (err: any) {
        console.error('Code verification error:', err);

        if (err.errors?.[0]?.code === 'verification_code_invalid') {
          setError(
            t(
              'auth_signin_error_code_invalid',
              'Der eingegebene Code ist ungültig. Bitte prüfen Sie, ob Sie den neuesten Code verwenden.',
            ),
          );
        } else if (err.errors?.[0]?.code === 'verification_code_expired') {
          setError(
            t(
              'auth_signin_error_code_expired',
              'Der Code ist abgelaufen. Bitte fordern Sie einen neuen Code an.',
            ),
          );
        } else {
          setError(
            err.errors?.[0]?.message ||
              t('auth_signin_error_code_generic', 'Ungültiger Code'),
          );
        }
        isVerifyingRef.current = false;
        setIsLoading(false);
      }
    },
    [
      signIn,
      signUp,
      setActive,
      dashboardUrl,
      authFlow,
      goToDashboard,
      t,
    ],
  );

  const handleGoogleOAuth = useCallback(async () => {
    try {
      const callbackUrl = window.location.origin + '/sso/callback/google';

      await signIn.authenticateWithRedirect({
        strategy: 'oauth_google',
        redirectUrl: callbackUrl,
        redirectUrlComplete: dashboardUrl,
      });
    } catch (error: any) {
      console.error('Google OAuth failed:', error);
      setError(
        t(
          'auth_signin_error_google_unavailable',
          'Google OAuth ist in der Entwicklungsumgebung nicht verfügbar',
        ),
      );
    }
  }, [
    signIn,
    dashboardUrl,
    t,
  ]);

  const handleAppleOAuth = useCallback(async () => {
    try {
      const callbackUrl = window.location.origin + '/sso/callback/apple';

      await signIn.authenticateWithRedirect({
        strategy: 'oauth_apple',
        redirectUrl: callbackUrl,
        redirectUrlComplete: dashboardUrl,
      });
    } catch (error: any) {
      console.error('Apple OAuth failed:', error);
      setError(
        t(
          'auth_signin_error_apple_unavailable',
          'Apple OAuth ist in der Entwicklungsumgebung nicht verfügbar',
        ),
      );
    }
  }, [
    signIn,
    dashboardUrl,
    t,
  ]);

  const handleValiantSSO = useCallback(async () => {
    setIsLoading(true);
    setError('');
    setSsoMessage(null);

    // Clear any stale Clerk state before starting SSO to prevent race conditions
    clearStaleClerkState();

    try {
      const callbackUrl = window.location.origin + '/sso/callback/valiant';

      await signIn.authenticateWithRedirect({
        strategy: 'saml',
        identifier: 'sso@valiant.ch',
        redirectUrl: callbackUrl,
        redirectUrlComplete: dashboardUrl,
      } as any);
    } catch (error: any) {
      console.error('Valiant SSO failed:', error);

      const errorCode = error.errors?.[0]?.code || error.code;

      if (
        process.env.NODE_ENV === 'development' &&
        errorCode === 'strategy_not_found'
      ) {
        setError(
          t(
            'auth_signin_error_valiant_not_configured',
            'Valiant SAML SSO ist in der Entwicklungsumgebung wahrscheinlich nicht konfiguriert.',
          ),
        );
      } else if (
        errorCode === 'session_exists' ||
        error.message?.includes('session')
      ) {
        // Session conflict - clear state and suggest retry
        clearStaleClerkState();
        setError(
          t(
            'auth_signin_error_session_exists',
            'Es besteht bereits eine aktive Session. Bitte laden Sie die Seite neu und versuchen Sie es erneut.',
          ),
        );
      } else if (errorCode === 'identifier_already_signed_in') {
        // User is already signed in somewhere - redirect to dashboard
        goToDashboard();
        return;
      } else {
        setError(
          error.errors?.[0]?.message ||
            t(
              'auth_signin_error_valiant_unavailable',
              'Valiant SSO ist nicht verfügbar',
            ),
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    signIn,
    dashboardUrl,
    goToDashboard,
    t,
  ]);

  const handleEmailSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setIsLoading(true);
      setError('');

      try {
        const result = await signIn.create({
          identifier: email,
        });

        if (result.status === 'needs_first_factor') {
          const emailCodeFactor = result.supportedFirstFactors?.find(
            (factor) =>
              factor.strategy === 'email_code' &&
              factor.safeIdentifier === email,
          ) as any;

          if (emailCodeFactor) {
            try {
              await signIn.prepareFirstFactor({
                strategy: 'email_code',
                emailAddressId: emailCodeFactor.emailAddressId,
              });

              setAuthFlow('signIn');
              setStep('code');
            } catch (prepareErr: any) {
              console.error('prepareFirstFactor error:', prepareErr);

              if (
                prepareErr.errors?.[0]?.code === 'verification_email_not_sent'
              ) {
                setError(
                  process.env.NODE_ENV === 'development'
                    ? 'E-Mail-Versand fehlgeschlagen. Bitte Clerk Development Keys korrekt konfigurieren.'
                    : t(
                        'auth_signin_error_email_not_sent',
                        'E-Mail-Versand fehlgeschlagen. Bitte versuchen Sie es später erneut.',
                      ),
                );
              } else {
                setError(
                  t(
                    'auth_signin_error_code_send_failed',
                    'Fehler beim Senden des Verifizierungscodes: {reason}',
                    {
                      reason:
                        prepareErr.errors?.[0]?.message ||
                        prepareErr.message ||
                        t('auth_signin_error_unknown', 'Unbekannter Fehler'),
                    },
                  ),
                );
              }
            }
          } else {
            const anyEmailFactor = result.supportedFirstFactors?.find(
              (factor) => factor.strategy === 'email_code',
            ) as any;

            if (anyEmailFactor) {
              await signIn.prepareFirstFactor({
                strategy: 'email_code',
                emailAddressId: anyEmailFactor.emailAddressId,
              });
              setAuthFlow('signIn');
              setStep('code');
            } else {
              setError(
                t(
                  'auth_signin_error_no_email_verification',
                  'Keine E-Mail-Verifizierung verfügbar.',
                ),
              );
            }
          }
        } else if (
          result.status === 'needs_verification' ||
          result.status === 'needs_second_factor'
        ) {
          setAuthFlow('signIn');
          setStep('code');
        } else {
          setError(
            t(
              'auth_signin_error_start_failed',
              'Anmeldung konnte nicht gestartet werden. Bitte versuchen Sie es erneut.',
            ),
          );
        }
      } catch (err: any) {
        console.error('Login error:', err);

        if (err.errors?.[0]?.code === 'form_identifier_not_found') {
          console.log('User not found, switching to Sign Up flow');
          try {
            await signUp.create({
              emailAddress: email,
              legalAccepted: true,
            });

            await signUp.prepareEmailAddressVerification({
              strategy: 'email_code',
            });

            setAuthFlow('signUp');
            setStep('code');
          } catch (signUpErr: any) {
            console.error('Sign Up error:', signUpErr);

            if (signUpErr.errors?.[0]?.code === 'verification_email_not_sent') {
              setError(
                process.env.NODE_ENV === 'development'
                  ? 'Registrierung fehlgeschlagen: E-Mail konnte nicht gesendet werden. Bitte Clerk Development Keys konfigurieren.'
                  : t(
                      'auth_signin_error_signup_email_not_sent',
                      'Registrierung fehlgeschlagen: E-Mail konnte nicht gesendet werden. Bitte versuchen Sie es später erneut.',
                    ),
              );
            } else if (isTurnstileError(signUpErr)) {
              setError(t(TURNSTILE_USER_MESSAGE_KEY, TURNSTILE_USER_MESSAGE));
            } else {
              setError(
                signUpErr.errors?.[0]?.message ||
                  t(
                    'auth_signin_error_signup_failed',
                    'Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.',
                  ),
              );
            }
          }
        } else if (err.errors?.[0]?.code === 'verification_email_not_sent') {
          setError(
            process.env.NODE_ENV === 'development'
              ? 'Der Verifizierungscode konnte nicht gesendet werden. Bitte überprüfen Sie Ihre Clerk-Konfiguration.'
              : t(
                  'auth_signin_error_verification_code_not_sent',
                  'Der Verifizierungscode konnte nicht gesendet werden. Bitte versuchen Sie es später erneut oder kontaktieren Sie den Support.',
                ),
          );
        } else if (err.errors?.[0]?.code === 'form_password_invalid') {
          // Bestehender Key aus dem alten Login-Flow — die Formulierung nennt
          // bewusst nicht, welches der beiden Felder falsch war.
          setError(
            t(
              'login_wrongpassword',
              'Passwort oder E-Mailadresse nicht korrekt',
            ),
          );
        } else if (isTurnstileError(err)) {
          setError(t(TURNSTILE_USER_MESSAGE_KEY, TURNSTILE_USER_MESSAGE));
        } else {
          setError(
            err.errors?.[0]?.message ||
              t(
                'auth_signin_error_unexpected',
                'Ein unerwarteter Fehler ist aufgetreten.',
              ),
          );
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      email,
      signIn,
      signUp,
      dashboardUrl,
      t,
    ],
  );

  const handleCodeSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (code.length === 6 && !isVerifyingRef.current) {
        await verifyCode(code);
      }
    },
    [
      code,
      verifyCode,
    ],
  );

  const goBack = useCallback(() => {
    setStep('email');
    setCode('');
    setError('');
    isVerifyingRef.current = false;
  }, []);

  if (!isLoaded) {
    return <LoadingSpinner mainLoader />;
  }

  const renderProviderIcon = (providerName: string) => {
    switch (providerName) {
      case 'google':
        return (
          <svg
            className="w-4 h-4"
            viewBox="0 0 24 24"
          >
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
        );
      case 'apple':
        return (
          <svg
            className="w-4 h-4"
            viewBox="0 0 24 24"
          >
            <path d="M17.05 20.28c-.98.95-2.05.88-3.08.35-1.09-.56-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
          </svg>
        );
      case 'valiant':
        return (
          <svg
            className="w-4 h-4"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <text
              x="50%"
              y="50%"
              dominantBaseline="central"
              textAnchor="middle"
              fontSize="14"
              fontWeight="bold"
              fontFamily="Arial, sans-serif"
            >
              V
            </text>
          </svg>
        );
      default:
        return (
          <svg
            className="w-4 h-4"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
          </svg>
        );
    }
  };

  return (
    <div className="flex items-start justify-center py-8 min-h-full">
      {isWaitingForHubspot && (
        <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
          <div className="px-6 text-center max-w-sm">
            <LoadingSpinner className="mx-auto mb-4" />
            <p className="text-base font-medium text-gray-900">
              {hubspotTimedOut
                ? t(
                    'auth_signin_preparing_slow',
                    'Die Vorbereitung dauert länger als erwartet. Bitte laden Sie die Seite neu.',
                  )
                : t('auth_signin_preparing', 'Ihr Konto wird vorbereitet...')}
            </p>
            {hubspotTimedOut && (
              <button
                className="mt-6 w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-900 transition-colors duration-200 min-h-[44px]"
                onClick={() => window.location.reload()}
              >
                {t('auth_signin_reload', 'Neu laden')}
              </button>
            )}
          </div>
        </div>
      )}
      <div className="max-w-md w-full">
        <div className="rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 shadow-sm dark:shadow-gray-900/50 p-4 sm:p-8 space-y-4 sm:space-y-6 overflow-visible">
          {isSSOView ? (
            <div className="space-y-6">
              <div className="text-center">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {t('auth_signin_valiant_title', 'Valiant Login')}
                </h1>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  {t(
                    'auth_signin_valiant_subtitle',
                    'Anmeldung für Valiant Mitarbeitende',
                  )}
                </p>
              </div>

              {ssoMessage && (
                <div className="text-amber-700 text-sm text-center p-3 bg-amber-50 rounded-lg border border-amber-200">
                  {ssoMessage}
                </div>
              )}

              {error && (
                <div className="text-red-600 text-sm text-center p-3 bg-red-50 rounded-lg">
                  {error}
                </div>
              )}

              <button
                onClick={handleValiantSSO}
                disabled={isLoading}
                className="w-full flex items-center justify-center px-4 py-3 bg-[#7252AC] border border-[#7252AC] rounded-lg text-white text-base font-medium hover:bg-[#5B418B] transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7252AC] focus-visible:ring-offset-2 group shadow-sm dark:shadow-gray-900/50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <span className="font-semibold">
                    {t('auth_signin_connecting', 'Verbinde...')}
                  </span>
                ) : (
                  <>
                    <span className="mr-3 group-hover:scale-110 transition-transform duration-200">
                      {renderProviderIcon('valiant')}
                    </span>
                    <span className="font-semibold">
                      {t('auth_signin_valiant_button', 'Mit Valiant anmelden')}
                    </span>
                  </>
                )}
              </button>

              <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
                <button
                  onClick={() => {
                    setIsSSOView(false);
                    setSsoMessage(null);
                    setError('');
                  }}
                  className="w-full text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 dark:text-gray-300 dark:hover:text-gray-300 font-medium transition-colors"
                >
                  {t(
                    'auth_signin_back_to_standard',
                    'Zurück zur Standard-Anmeldung',
                  )}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="text-center">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {t('auth_signin_title', 'Weiter zu AgentSelly')}
                </h1>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  {t(
                    'auth_signin_subtitle',
                    'Melden Sie sich mit Ihrem Konto an.',
                  )}
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleGoogleOAuth}
                  className="flex-1 flex items-center justify-center px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-200 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-800 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-600 dark:border-gray-600 dark:hover:border-gray-600 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 group"
                >
                  <span className="mr-2 group-hover:scale-110 transition-transform duration-200">
                    {renderProviderIcon('google')}
                  </span>
                  <span className="font-semibold">
                    {t('auth_provider_google', 'Google')}
                  </span>
                </button>

                <button
                  onClick={handleAppleOAuth}
                  className="flex-1 flex items-center justify-center px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-200 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-800 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-600 dark:border-gray-600 dark:hover:border-gray-600 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 group"
                >
                  <span className="mr-2 group-hover:scale-110 transition-transform duration-200">
                    {renderProviderIcon('apple')}
                  </span>
                  <span className="font-semibold">
                    {t('auth_provider_apple', 'Apple')}
                  </span>
                </button>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 font-medium">
                    {t('auth_divider_or', 'oder')}
                  </span>
                </div>
              </div>

              <div className="space-y-4">
                {step === 'email' ? (
                  <>
                    <h3 className="text-center text-lg font-medium text-gray-900 dark:text-gray-100">
                      {t(
                        'auth_signin_email_code_title',
                        'E-Mail Code Anmeldung',
                      )}
                    </h3>

                    <form
                      onSubmit={handleEmailSubmit}
                      className="space-y-3 sm:space-y-4"
                    >
                      <div>
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-200 dark:text-gray-300"
                        >
                          {t('auth_email_label', 'E-Mail-Adresse')}
                        </label>
                        <input
                          id="email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          className="mt-1 block w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm dark:shadow-gray-900/50 placeholder-gray-400 dark:placeholder-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:border-transparent dark:bg-gray-800 dark:text-gray-100"
                          placeholder={t(
                            'auth_signin_email_placeholder',
                            'E-Mail Adresse eingeben',
                          )}
                        />
                      </div>

                      <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                        <T
                          keyName="auth_signin_legal_note"
                          defaultValue="Wenn Sie noch kein Konto haben, wird eines erstellt. Mit der Registrierung akzeptieren Sie die <terms>AGB</terms> und die <privacy>Datenschutzrichtlinie</privacy>."
                          params={{
                            terms: (
                              <a
                                href="https://www.agentselly.ch/agb/"
                                target="_blank"
                                rel="noreferrer"
                                className="text-primary-500 hover:text-primary-400 font-medium"
                              />
                            ),
                            privacy: (
                              <a
                                href="https://www.agentselly.ch/datenschutz/"
                                target="_blank"
                                rel="noreferrer"
                                className="text-primary-500 hover:text-primary-400 font-medium"
                              />
                            ),
                          }}
                        />
                      </p>

                      {error && (
                        <div className="text-red-600 text-sm text-center p-3 bg-red-50 rounded-lg">
                          {error}
                        </div>
                      )}

                      {/* Clerk CAPTCHA container */}
                      <div id="clerk-captcha" />

                      <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm dark:shadow-gray-900/50 text-sm font-medium text-white bg-gray-900 dark:bg-gray-700 hover:bg-gray-800 dark:hover:bg-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                      >
                        {isLoading
                          ? t('auth_sending_code', 'Code wird gesendet...')
                          : t('auth_signin_continue', 'Fortsetzen')}
                      </button>
                    </form>

                    {initialSSOView && (
                      <div className="pt-2 sm:pt-4 text-center">
                        <button
                          onClick={() => setIsSSOView(true)}
                          className="text-xs text-gray-400 dark:text-gray-500 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-400 dark:text-gray-400 dark:hover:text-gray-400 transition-colors"
                        >
                          {t('auth_signin_sso_link', 'SSO Anmeldung')}
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="text-center">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                        {t('auth_signin_code_title', 'Code eingeben')}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        <T
                          keyName="auth_code_sent_to"
                          defaultValue="Wir haben einen Verifizierungscode an <strong>{email}</strong> gesendet"
                          params={{
                            email,
                            strong: <strong />,
                          }}
                        />
                      </p>
                      {process.env.NODE_ENV === 'development' && (
                        <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                          <strong>ENTWICKLUNGSMODUS:</strong> Wenn der Code
                          nicht ankommt, bitte
                          <a
                            href="https://dashboard.clerk.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline"
                          >
                            Clerk Dashboard
                          </a>
                          für Test-Codes überprüfen.
                        </div>
                      )}
                    </div>

                    <form
                      onSubmit={handleCodeSubmit}
                      className="space-y-3 sm:space-y-4"
                    >
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 dark:text-gray-300 mb-3 text-center">
                          {t('auth_code_label', 'Verifizierungscode')}
                        </label>
                        <LegacyOtpInput
                          value={code}
                          onChange={setCode}
                          onComplete={verifyCode}
                          length={6}
                          disabled={isLoading}
                        />
                      </div>

                      {error && (
                        <div className="text-red-600 text-sm text-center p-3 bg-red-50 rounded-lg">
                          {error}
                        </div>
                      )}

                      {/* Clerk CAPTCHA container */}
                      <div id="clerk-captcha-step-2" />

                      <div className="flex gap-3 space-x-3 mt-4 w-full shrink-0">
                        <button
                          type="button"
                          onClick={goBack}
                          className="flex-1 flex justify-center py-3 px-4 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm dark:shadow-gray-900/50 text-sm font-medium text-gray-700 dark:text-gray-200 dark:text-gray-300 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-800 dark:hover:bg-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary-500 transition-colors duration-200 min-h-[44px]"
                        >
                          {t('auth_back', 'Zurück')}
                        </button>
                        <button
                          type="submit"
                          disabled={isLoading || code.length !== 6}
                          className="flex-1 flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm dark:shadow-gray-900/50 text-sm font-medium text-white bg-gray-900 dark:bg-gray-700 hover:bg-gray-800 dark:hover:bg-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 min-h-[44px]"
                        >
                          {t('auth_signin_submit', 'Anmelden')}
                        </button>
                      </div>
                    </form>

                    <div className="text-center mt-6">
                      <button
                        onClick={goBack}
                        className="inline-block py-2 text-sm text-primary-500 hover:text-primary-400 font-medium"
                      >
                        {t('auth_resend_code', 'Code erneut senden')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
